<template>
  <div id="app" class="h-screen flex flex-col overflow-hidden">
    <!-- Global background -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none z-0">
      <div class="absolute top-[-10%] left-[10%] w-[500px] h-[500px] bg-blue-500/15 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 animate-blob"></div>
      <div class="absolute top-[30%] right-[-10%] w-[400px] h-[400px] bg-cyan-400/15 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 animate-blob animation-delay-2000"></div>
      <div class="absolute bottom-[-10%] left-[30%] w-[600px] h-[600px] bg-indigo-500/10 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 animate-blob animation-delay-4000"></div>
    </div>

    <!-- Header -->
    <AppHeader />

    <!-- Main content -->
    <main class="flex-1 flex overflow-hidden p-6 gap-6 relative max-w-[1700px] mx-auto w-full z-10">
      <!-- Left workflow navigation - only show for intelligent engine pages -->
      <div v-if="showWorkflowNav" class="flex-shrink-0 transition-all duration-300" :class="workflowNavCollapsed ? 'w-[60px]' : 'w-[280px]'">
        <div class="glass-card rounded-2xl h-full flex flex-col overflow-hidden">
          <!-- Collapse Toggle Header -->
          <div class="p-4 border-b border-slate-100">
            <div class="flex items-center justify-between mb-1">
              <div v-if="!workflowNavCollapsed" class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                  </svg>
                </div>
                <span class="font-bold text-slate-800 text-sm">工作流</span>
              </div>
              <button 
                @click="workflowNavCollapsed = !workflowNavCollapsed"
                class="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
                :class="workflowNavCollapsed ? 'mx-auto' : ''"
              >
                <svg 
                  class="w-5 h-5 text-slate-500 transition-transform duration-300" 
                  :class="workflowNavCollapsed ? 'rotate-180' : ''"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>
            </div>
            <!-- Subtitle -->
            <div v-if="!workflowNavCollapsed" class="text-xs text-slate-500 ml-10">
              全流程智能评审
            </div>
          </div>

          <!-- Workflow Content -->
          <div v-if="!workflowNavCollapsed" class="flex-1 overflow-y-auto p-3">
            <div class="space-y-2">
              <div
                v-for="(item, index) in workflowItems"
                :key="item.id"
                class="workflow-item"
                :style="{ animationDelay: index * 0.05 + 's' }"
              >
                <!-- Main Node -->
                <div
                  class="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                  :class="[
                    activeWorkflowIndex === index 
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-lg shadow-blue-500/25' 
                      : 'hover:bg-slate-50 text-slate-600'
                  ]"
                  @click="selectWorkflowItem(index)"
                >
                  <div 
                    class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all duration-300"
                    :class="[
                      item.status === 'completed' 
                        ? 'bg-emerald-400 text-white' 
                        : activeWorkflowIndex === index 
                          ? 'bg-white/20 text-white scale-110' 
                          : 'bg-slate-200 text-slate-500'
                    ]"
                  >
                    <span v-if="item.status === 'completed'">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </span>
                    <span v-else>{{ item.number }}</span>
                  </div>
                  <span class="text-sm font-medium flex-1 truncate">{{ item.title }}</span>
                  <svg 
                    v-if="item.subTasks && item.subTasks.length > 0"
                    class="w-4 h-4 transition-transform duration-300"
                    :class="item.expanded ? 'rotate-90' : ''"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </div>

                <!-- Sub Tasks -->
                <transition 
                  enter-active-class="transition-all duration-300 ease-out"
                  enter-from-class="opacity-0 max-h-0"
                  enter-to-class="opacity-100 max-h-96"
                  leave-active-class="transition-all duration-200 ease-in"
                  leave-from-class="opacity-100 max-h-96"
                  leave-to-class="opacity-0 max-h-0"
                >
                  <div 
                    v-show="item.subTasks && item.subTasks.length > 0 && item.expanded" 
                    class="mt-1 ml-5 pl-4 border-l-2 border-slate-200 space-y-1 overflow-hidden"
                  >
                    <div
                      v-for="(sub, subIndex) in item.subTasks"
                      :key="sub.id"
                      class="flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all duration-200 text-xs hover:translate-x-1"
                      :class="[
                        sub.active 
                          ? 'bg-blue-50 text-blue-600 font-medium' 
                          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                      ]"
                      @click.stop="selectSubTask(index, subIndex)"
                      :style="{ animationDelay: subIndex * 0.03 + 's' }"
                    >
                      <div 
                        class="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all duration-200"
                        :class="sub.active ? 'bg-blue-500 scale-125' : 'bg-slate-300'"
                      ></div>
                      <span class="truncate">{{ sub.title }}</span>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <!-- Collapsed State - Icons Only -->
          <div v-else class="flex-1 overflow-y-auto py-3 space-y-2">
            <div
              v-for="(item, index) in workflowItems"
              :key="item.id"
              class="flex justify-center"
              :style="{ animationDelay: index * 0.05 + 's' }"
            >
              <div
                class="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110"
                :class="[
                  activeWorkflowIndex === index 
                    ? 'bg-gradient-to-br from-blue-500 to-cyan-400 text-white shadow-lg shadow-blue-500/30 scale-110' 
                    : item.status === 'completed'
                      ? 'bg-emerald-100 text-emerald-600'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                ]"
                @click="selectWorkflowItem(index)"
                :title="item.title"
              >
                <span v-if="item.status === 'completed'" class="text-sm font-bold">✓</span>
                <span v-else class="text-sm font-bold">{{ item.number }}</span>
              </div>
            </div>
          </div>

          <!-- Footer Progress -->
          <div v-if="!workflowNavCollapsed" class="p-4 border-t border-slate-100">
            <div class="flex items-center justify-between text-xs mb-2">
              <span class="text-slate-500">当前环节</span>
              <span class="font-semibold text-slate-700">{{ activeWorkflowIndex + 1 }} / {{ workflowItems.length }}</span>
            </div>
            <div class="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div 
                class="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-300" 
                :style="{ width: ((activeWorkflowIndex + 1) / workflowItems.length * 100) + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right content area -->
      <div class="flex-1 overflow-y-auto relative z-10 pb-10">
        <!-- Workflow Description Panel - Show when workflow nav is collapsed -->
        <div v-if="showWorkflowNav && workflowNavCollapsed" class="mb-6">
          <div class="glass-card rounded-2xl p-6 bg-gradient-to-r from-blue-50/80 via-white to-cyan-50/80">
            <div class="flex items-start gap-6">
              <!-- Current Stage Icon -->
              <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                <span class="text-2xl font-bold text-white">{{ currentWorkflowItem.number }}</span>
              </div>
              
              <!-- Stage Info -->
              <div class="flex-1">
                <div class="flex items-center gap-3 mb-2">
                  <h2 class="text-xl font-bold text-slate-800">{{ currentWorkflowItem.title }}</h2>
                  <span 
                    class="px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="currentWorkflowItem.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'"
                  >
                    {{ currentWorkflowItem.status === 'completed' ? '已完成' : '进行中' }}
                  </span>
                </div>
                <p class="text-sm text-slate-600 mb-4">{{ currentWorkflowItem.description }}</p>
                
                <!-- Sub Tasks Preview -->
                <div v-if="currentWorkflowItem.subTasks && currentWorkflowItem.subTasks.length > 0" class="flex flex-wrap gap-2">
                  <div
                    v-for="(sub, idx) in currentWorkflowItem.subTasks.slice(0, 4)"
                    :key="sub.id"
                    class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
                    :class="sub.active ? 'bg-blue-500 text-white' : 'bg-white border border-slate-200 text-slate-600'"
                  >
                    <span 
                      class="w-1.5 h-1.5 rounded-full"
                      :class="sub.active ? 'bg-white' : 'bg-slate-300'"
                    ></span>
                    {{ sub.title }}
                  </div>
                  <div v-if="currentWorkflowItem.subTasks.length > 4" class="px-3 py-1.5 rounded-lg text-xs bg-slate-100 text-slate-500">
                    +{{ currentWorkflowItem.subTasks.length - 4 }} 更多
                  </div>
                </div>
              </div>

              <!-- Quick Actions -->
              <div class="flex flex-col gap-2">
                <button 
                  @click="workflowNavCollapsed = false"
                  class="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                  </svg>
                  展开导航
                </button>
                <div class="text-xs text-slate-400 text-center">
                  按环节 {{ activeWorkflowIndex + 1 }}/{{ workflowItems.length }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <router-view />
      </div>
    </main>
  </div>
</template>

<script>
import AppHeader from './components/AppHeader.vue'

export default {
  name: 'App',
  components: {
    AppHeader
  },
  data() {
    return {
      workflowNavCollapsed: true, // 默认收起
      activeWorkflowIndex: 1, // 默认选中第2个（申报初审）
      workflowItems: [
        {
          id: 1,
          number: 1,
          title: '指南凝练',
          description: '通过AI技术优化项目指南方向，分析前沿技术趋势，确保指南指标科学合理，避免重复立项',
          status: 'completed',
          expanded: false,
          subTasks: [
            { id: '1-1', title: '指南方向优化', description: '与前沿技术方向对比', active: false },
            { id: '1-2', title: '指南指标优化', description: '关键指标量化分析', active: false },
            { id: '1-3', title: '指南内容查重', description: '避免重复立项', active: false },
            { id: '1-4', title: '指标偏低分析', description: '与顶层方案对比', active: false }
          ]
        },
        {
          id: 2,
          number: 2,
          title: '申报初审',
          description: 'AI自动审核申报材料完整性、规范性，检测与指南一致性，防范科研诚信风险',
          status: 'active',
          expanded: true,
          subTasks: [
            { id: '2-1', title: '与指南一致性分析', description: '研究方向契合度检测', active: true },
            { id: '2-2', title: '申报书内容查重', description: '历史项目相似度分析', active: false },
            { id: '2-3', title: '科研诚信核查', description: '失信与撤稿记录', active: false },
            { id: '2-4', title: '申报书格式审查', description: '格式规范性检查', active: false },
            { id: '2-5', title: '经费编制审查', description: '预算合理性分析', active: false },
            { id: '2-6', title: '科技敏感问题审查', description: '伦理与机密审查', active: false },
            { id: '2-7', title: '项目限项审查', description: '在研项目数量核查', active: false }
          ]
        },
        {
          id: 3,
          number: 3,
          title: '立项评审',
          description: '智能匹配评审专家，AI辅助内容评审，确保评审公平公正',
          status: 'pending',
          expanded: false,
          subTasks: [
            { id: '3-1', title: '内容评审', description: '申报书深度分析', active: false },
            { id: '3-2', title: '专家遴选', description: '智能匹配评审专家', active: false }
          ]
        },
        {
          id: 4,
          number: 4,
          title: '过程检查',
          description: '动态监控项目执行进度，AI分析任务书与指南合同一致性，及时发现执行偏差',
          status: 'pending',
          expanded: false,
          subTasks: [
            { id: '4-1', title: '任务书分析', description: '与指南合同对比', active: false },
            { id: '4-2', title: '项目执行情况分析', description: '进度与经费追踪', active: false }
          ]
        },
        {
          id: 5,
          number: 5,
          title: '结题审查',
          description: 'AI智能复核验收报告，核查成果真实性，评估任务完成情况',
          status: 'pending',
          expanded: false,
          subTasks: [
            { id: '5-1', title: '项目执行情况审查', description: '任务完成情况评估', active: false },
            { id: '5-2', title: '经费执行情况审查', description: '经费使用合规性', active: false },
            { id: '5-3', title: '项目成果审查', description: '成果真实性核查', active: false }
          ]
        }
      ]
    }
  },
  computed: {
    showWorkflowNav() {
      // Only show workflow nav for intelligent engine pages, not for dashboard, ai-assistant, project management or data management pages
      const path = this.$route.path
      return path !== '/' && !path.includes('/ai-assistant') && !path.includes('/project-list') && !path.includes('/process-management') && !path.includes('/knowledge-base')
    },
    currentWorkflowItem() {
      return this.workflowItems[this.activeWorkflowIndex] || this.workflowItems[0]
    }
  },
  methods: {
    selectWorkflowItem(index) {
      this.activeWorkflowIndex = index
      
      // Toggle expanded state
      this.workflowItems.forEach((item, i) => {
        if (i === index) {
          item.expanded = !item.expanded
        } else {
          item.expanded = false
        }
      })
      
      // Route to corresponding page - 点击主节点时默认跳转到该阶段的第一个子任务页面
      const routes = {
        0: '/guide-refine',        // 指南凝练 -> 指南方向优化
        1: '/declare-audit',       // 申报初审 -> 申报书内容查重 (2-2)
        2: '/project-review',      // 立项评审 -> AI辅助评审
        3: '/process-check',       // 过程检查 -> 任务书分析
        4: '/final-audit'          // 结题审查 -> 项目执行情况审查
      }
      const route = routes[index]
      if (route && this.$route.path !== route) {
        this.$router.push(route)
      }
    },
    selectSubTask(mainIndex, subIndex) {
      this.activeWorkflowIndex = mainIndex
      
      // Ensure the main item is expanded
      this.workflowItems.forEach((item, i) => {
        item.expanded = (i === mainIndex)
        item.subTasks.forEach((sub, j) => {
          sub.active = (i === mainIndex && j === subIndex)
        })
      })

      // Route to corresponding page - 按阶段分组路由
      const routes = {
        // 指南凝练阶段 - 所有子任务都指向 guide-refine
        '1-1': '/guide-refine',
        '1-2': '/guide-refine',
        '1-3': '/guide-refine',
        '1-4': '/guide-refine',
        // 申报初审阶段
        '2-1': '/consistency-analysis',
        '2-2': '/declare-duplicate-check',
        '2-3': '/research-integrity',
        '2-4': '/declare-format-check',
        '2-5': '/declare-budget-check',
        '2-6': '/declare-sensitive-check',
        '2-7': '/declare-limit-check',
        // 立项评审阶段
        '3-1': '/project-review?tab=content',
        '3-2': '/project-review?tab=expert',
        // 过程检查阶段
        '4-1': '/process-check?tab=task',
        '4-2': '/process-check?tab=execution',
        // 结题审查阶段
        '5-1': '/final-audit',
        '5-2': '/final-audit',
        '5-3': '/final-audit'
      }

      const subTaskId = this.workflowItems[mainIndex].subTasks[subIndex].id
      const route = routes[subTaskId]
      if (route && this.$route.path !== route) {
        this.$router.push(route)
      }
    }
  }
}
</script>

<style>
/* Global animations */
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

/* Workflow item animations */
.workflow-item {
  animation: fadeInUp 0.4s ease-out forwards;
  opacity: 0;
  transform: translateY(10px);
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Sub task stagger animation */
.workflow-item .space-y-1 > div {
  animation: slideInLeft 0.3s ease-out forwards;
  opacity: 0;
  transform: translateX(-10px);
}

@keyframes slideInLeft {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Active node pulse animation */
.bg-gradient-to-r.from-blue-500.to-cyan-400 .w-7 {
  animation: pulse-ring 2s ease-out infinite;
}

@keyframes pulse-ring {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

/* Hover lift effect */
.workflow-item > div:first-child:hover {
  transform: translateY(-2px);
}

/* Completed status glow */
.bg-emerald-400 {
  box-shadow: 0 0 12px rgba(52, 211, 153, 0.4);
}
</style>
